/**
 * CateringVendorService — vendor browsing and selection state.
 *
 * RESPONSIBILITY: Loads vendors for a location, applies client-side filters
 * (tags, open/closed, featured-only), groups filtered vendors into Featured /
 * Open now / Others sections, and manages selected-vendor state. Does NOT
 * manage menus, carts, or orders — those are separate services.
 *
 * SIGNALS:
 *   vendors (readonly)            — all vendors for current location
 *   filteredVendors (computed)    — after tag/open/featured filters applied
 *   groupedVendors (computed)     — { featured, openNow, others } buckets of filteredVendors
 *   selectedVendor (readonly)     — currently selected vendor
 *   tagFilter (readonly)          — active tag filters
 *   openOnlyFilter (readonly)     — show only open vendors
 *   featuredOnlyFilter (readonly) — show only featured vendors
 *   availableTags (computed)      — unique tags across all vendors (for filter chips)
 *   loading (readonly)            — loading state
 *   error (readonly)              — error message
 *
 * USED BY: VendorListComponent, VendorCardComponent, MenuPanelComponent
 * BACKEND CONTRACT: cateringVendors query with tags/isOpen params
 *
 * AI CONTEXT: Filtering is done client-side after the initial fetch so that
 * toggling filters is instant. The API query supports server-side filtering
 * too, but we only use it for the initial load when a specific filter is
 * pre-applied (e.g., deep-linking to a tag). Grouping is computed from the
 * filtered list — a vendor appears in at most ONE bucket (featured > openNow > others).
 */
import { Injectable, inject, signal, computed } from '@angular/core';
import { CateringApiService } from './catering-api.service';
import { CateringVendor } from '../models/catering.model';
import { isVendorOpen } from '../utils/catering';

export interface VendorGroups {
  featured: CateringVendor[];
  openNow: CateringVendor[];
  others: CateringVendor[];
}

@Injectable({ providedIn: 'root' })
export class CateringVendorService {
  private readonly api = inject(CateringApiService);

  // ── Private signals ─────────────────────────────────

  private readonly _vendors = signal<CateringVendor[]>([]);
  private readonly _selectedVendor = signal<CateringVendor | null>(null);
  private readonly _tagFilter = signal<string[]>([]);
  private readonly _openOnlyFilter = signal(false);
  private readonly _featuredOnlyFilter = signal(false);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  // ── Public readonly signals ─────────────────────────

  readonly vendors = this._vendors.asReadonly();
  readonly selectedVendor = this._selectedVendor.asReadonly();
  readonly tagFilter = this._tagFilter.asReadonly();
  readonly openOnlyFilter = this._openOnlyFilter.asReadonly();
  readonly featuredOnlyFilter = this._featuredOnlyFilter.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // ── Computed signals ────────────────────────────────

  readonly filteredVendors = computed(() => {
    let vendors = this._vendors();
    const tags = this._tagFilter();
    const openOnly = this._openOnlyFilter();
    const featuredOnly = this._featuredOnlyFilter();

    if (tags.length > 0) {
      vendors = vendors.filter(v =>
        tags.some(tag => v.tags?.includes(tag)),
      );
    }

    if (openOnly) {
      vendors = vendors.filter(v => isVendorOpen(v.operatingHours));
    }

    if (featuredOnly) {
      vendors = vendors.filter(v => v.isFeatured);
    }

    return vendors;
  });

  /**
   * Groups the filtered vendors into three disjoint sections.
   * Precedence: featured > openNow > others (each vendor appears in exactly one bucket).
   */
  readonly groupedVendors = computed<VendorGroups>(() => {
    const groups: VendorGroups = { featured: [], openNow: [], others: [] };
    for (const v of this.filteredVendors()) {
      if (v.isFeatured) {
        groups.featured.push(v);
      } else if (isVendorOpen(v.operatingHours)) {
        groups.openNow.push(v);
      } else {
        groups.others.push(v);
      }
    }
    return groups;
  });

  readonly availableTags = computed(() => {
    const allTags = this._vendors().flatMap(v => v.tags ?? []);
    return Array.from(new Set(allTags)).sort();
  });

  // ── Methods ─────────────────────────────────────────

  /** Load all vendors for a location. */
  async loadVendors(locationId: string): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const vendors = await this.api.loadVendors(locationId);
      this._vendors.set(vendors);
    } catch (cause) {
      console.warn('[CateringVendor] loadVendors error:', cause);
      this._error.set((cause instanceof Error ? cause.message : null) || 'Failed to load vendors');
    } finally {
      this._loading.set(false);
    }
  }

  /** Select a vendor by ID. Fetches from the already-loaded list. */
  selectVendor(vendorId: string): void {
    const vendor = this._vendors().find(v => v._id === vendorId) ?? null;
    this._selectedVendor.set(vendor);
  }

  /** Clear vendor selection. */
  clearSelection(): void {
    this._selectedVendor.set(null);
  }

  /** Set tag filter (replaces current filter). */
  setTagFilter(tags: string[]): void {
    this._tagFilter.set(tags);
  }

  /** Toggle the open-only filter on/off. */
  toggleOpenOnly(): void {
    this._openOnlyFilter.update(v => !v);
  }

  /** Toggle the featured-only filter on/off. */
  toggleFeaturedOnly(): void {
    this._featuredOnlyFilter.update(v => !v);
  }

  /** Clear all state (vendors, selection, filters). */
  reset(): void {
    this._vendors.set([]);
    this._selectedVendor.set(null);
    this._tagFilter.set([]);
    this._openOnlyFilter.set(false);
    this._featuredOnlyFilter.set(false);
    this._loading.set(false);
    this._error.set(null);
  }
}
